<script setup lang="ts">
import { MediaImageFragment } from '~/api/graphql/generated'

interface RoomIcon {
  icon?: MediaImageFragment
  variant: string
  value: string
}

interface RoomOverviewItemProps {
  title?: string
  description?: string
  mainImage?: MediaImageFragment | null
  floorPlanImage?: MediaImageFragment | null
  icons?: RoomIcon[]
  cta?: {
    label: string
    url: string
    target: '_self' | '_blank'
  } | null
}

const props = withDefaults(defineProps<RoomOverviewItemProps>(), {
  title: '',
  description: '',
  mainImage: null,
  floorPlanImage: null,
  icons: () => [],
  cta: null,
})

// Always render an even number of icons (max 4). If there are 2, add a dummy 4th.
const statItems = computed(() => {
  const items = props.icons || []
  if (items.length === 2) {
    // Add a dummy icon that will render nothing
    return [...items, {}, {}] as RoomIcon[]
  }
  if (items.length === 3) {
    // If 3, add a dummy to make it 4 (optional, but for evenness)
    return [...items, {}] as RoomIcon[]
  }
  return items
})
</script>

<template>
  <div class="room-overview-item">
    <div class="room-image-section">
      <RoomImageSlider
        :main-image="props.mainImage"
        :floor-plan-image="props.floorPlanImage"
        image-type="room-overview"
      />
    </div>

    <div class="room-content-section">
      <div v-if="title || description" class="room-text">
        <h3 v-if="title" v-animate="{ animation: 'vertical-reveal' }" class="room-title h2" v-text="title" />
        <div
          v-if="description"
          v-animate="{ animation: 'vertical-reveal' }"
          class="room-description text-small"
          v-html="description"
        />
      </div>

      <div v-if="statItems && statItems.length" class="room-icons">
        <RoomIconItem v-for="(item, index) in statItems" :key="`icon-${index}`" v-bind="item" />
      </div>

      <div v-if="cta?.label && cta?.url" class="room-cta">
        <Button :href="cta.url" :target="cta.target" secondary slide>
          {{ cta.label }}
        </Button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.room-overview-item {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  align-items: start;

  @include breakpoint(tl) {
    grid-template-columns: repeat(11, minmax(0, 1fr));
    gap: var(--gap);
    align-items: center;
  }
}

.room-image-section {
  @include breakpoint(tl) {
    grid-column: 1 / 7;
    margin-right: 44px;
  }
}

.room-content-section {
  @include breakpoint(tl) {
    grid-column: 7 / 12;
  }
  display: flex;
  flex-direction: column;
  @include fluid(gap, 36px, 48px);
}

.room-text {
  .room-title {
    color: var(--c-primary);
    @include fluid(margin-bottom, 10px, 12px);
    hyphens: none;
    overflow-wrap: initial;
  }

  .room-description {
    color: var(--c-text-medium);

    :deep(*:last-child) {
      margin-bottom: 0;
    }
  }
}

.room-icons {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.room-icon-item {
  &:nth-child(2n + 1) {
    border-right: 1px solid var(--c-line-light);
  }
  &:nth-child(2n + 3),
  &:nth-child(2n + 4) {
    border-top: 1px solid var(--c-line-light);
  }

  &:nth-child(2n + 2) {
    @include fluid(padding-left, 12px, 24px);
  }

  @include fluid(padding-block, 12px, 24px);
  &:first-child {
    padding-top: 0;
    + .room-icon-item {
      padding-top: 0;
    }
  }
  &:last-child:nth-child(odd),
  &:last-child:nth-child(even),
  &:nth-last-child(2):nth-child(odd) {
    padding-bottom: 0;
  }
}

.room-cta {
  align-self: stretch;

  :deep(.button) {
    width: 100%;
    justify-content: center;
  }

  @include breakpoint(tl) {
    align-self: start;

    :deep(.button) {
      width: auto;
    }
  }
}
</style>
